// External globals
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { Codes } from '@/constants/codes';
import { AccessAuthorities } from '@/models';
import moment from 'moment';
import { hasValue, hasStringValue, getModuleType, currentUserHasPermission } from '@/utils/legacy-utils';

import { ModuleType } from '@/constants/codes';
import { hasNumericValue } from '@/utils/validation';
import { getCurrentUserId, getAuthObject } from '@/utils/auth';
import { CallCardStatus } from '@/services/api-types';
import { load } from '@/services/loading';
import { isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp, kstConfirm, kstPrompt, CallCard, Mousetrap, $, DATETIME_FORMAT_SERVER_UTC } from '@/utils/window-globals';
// Lazy window-global lookups — resolved at call time, not module load time,
// to avoid undefined captures when module evaluation order differs from registration order.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const goToInProgressScreen = _wg('goToInProgressScreen');
const setCallCardRealtimeEnabled = _wg('setCallCardRealtimeEnabled');
const setCallCardScreenLoaded = _wg('setCallCardScreenLoaded');
const subscribeToCallCardUpdates = _wg('subscribeToCallCardUpdates');
const isBetaTesting = _wg('isBetaTesting');
const showHomePage = _wg('showHomePage');
const cleanUpCallCard = _wg('cleanUpCallCard');
const currentISODateTime = _wg('currentISODateTime');

export const MDT_ACTIVE_CALL_TIMESTAMP_KEY = "CurrentCallCardTimestamp";
export let isInitialMDTModuleLoad = true;

// Self Initiate Modal — rendered via React bridge
export let _selfInitiateModalContainer: HTMLElement | null = null;
export let _selfInitiateModalOpen = false;

export function _renderSelfInitiateModal() {
    if (!_selfInitiateModalContainer) {
        _selfInitiateModalContainer = document.createElement('div');
        document.body.appendChild(_selfInitiateModalContainer);
    }

    (window as any).OrnaReact.renderComponent(
        (window as any).SelfInitiateModalReact,
        {
            open: _selfInitiateModalOpen,
            onInitiate: function (callCardId: number, isCleared: boolean) {
                _selfInitiateModalOpen = false;
                _renderSelfInitiateModal();
                goToSelfInitiatedCall(callCardId, false, isCleared);
            },
            onCancel: function () {
                _selfInitiateModalOpen = false;
                _renderSelfInitiateModal();
            }
        },
        _selfInitiateModalContainer
    );
}

export function showSelfInitiateModal() {
    _selfInitiateModalOpen = true;
    _renderSelfInitiateModal();
}

export function goToSelfInitiatedCall(callCardId: number, overwriteExistingActiveCallCard: boolean = false, isCleared: boolean = false) {
    if (!isCleared) {
        // Prevent overwriting current call if there is a race condition,
        // and dispatcher assigned officer to call while officer was self initiating.
        addMDTActiveCallCard(callCardId, overwriteExistingActiveCallCard);
    }
    // Set to localStorage in case of update that hard refreshes the page
    localStorage.setItem("SelfInitiatedCallCard", callCardId.toString());

    window.OrnaReact.navigate.toCallCard(callCardId, 'MDT');
}

export function MDT_Home() {
    (window as any).initHomeScreen('Mobile Patrol Module', 'Mobile', HelpDeskType.MDTGettingStarted);
}

export function MDT_Selected() {
    MDT_Home();
}

export function MDT_OfficerStatus() {
    (window as any).setUpOfficerStatusScreen('mdt');
}


export function MDT_ShowCallCard(id: number) {
    resetCallCardClockRefresh();

    if (id !== undefined && id !== null && id > 0) {
        subscribeToCallCardUpdates(id, ModuleType.mdt);


        api.getAvailableCADOfficers(id, availableOfficersResult => {
            processCallCardAvailableOfficers(availableOfficersResult);

            api.getMDTCallCard(id.toString(), true, result => {
                App.currentCCOriginal = new CallCard(result, ModuleType.mdt, false);
                App.currentCC = new CallCard(result, ModuleType.mdt, true, availableOfficersResult.officerList);
                setCallCardScreenLoaded(true);
                setCallCardRealtimeEnabled(true);
            });
        });
    }
    else {
        App.currentCCOriginal = new CallCard(undefined, ModuleType.mdt, false);
        App.currentCC = new CallCard(undefined, ModuleType.mdt, true);
        setCallCardScreenLoaded(true);
        setCallCardRealtimeEnabled(true);
    }
}

export function updateMDTEditModeAllowed() {
    if (!App.callCardController) return;
    if (App.currentCC.ccStatus === CallCardStatus.CASE_CLOSED || !mdtHasPermissionsToEditCallCard()) {
        App.callCardController.editModeAllowed = false;
        App.callCardController.isEditMode = false;
    } else {
        App.callCardController.editModeAllowed = true;
    }
}

export function mdtHasPermissionsToEditCallCard() {
    let submodules: Array<string> = getAvailableSubmodules();
    let currentOfficerId = getCurrentUserId();

    // if the person who iniated the card is actually also the primary officer they can edit
    // even though they normally don't have that option.
    if (App.currentCC.author === currentOfficerId) {
        return true;
    }

    if (isInArray(submodules, AccessAuthorities.ROLE_MDT_CALL_CARD_UPDATE)) {
        return true;
    }
    if (App.currentCC.primaryOfficer === currentOfficerId) {
        if (isInArray(submodules, AccessAuthorities.ROLE_MDT_CALL_CARD_UPDATE_IF_PRIMARY)) {
            return true;
        }
    }
    let isBackupUnit = false;
    if (hasValue(App.currentCC.backupUnits) && App.currentCC.backupUnits.length > 0) {
        for (let backupUnit of App.currentCC.backupUnits) {
            if (backupUnit.officer === currentOfficerId) {
                isBackupUnit = true;
            }
        }
    }
    if (isBackupUnit && isInArray(submodules, AccessAuthorities.ROLE_MDT_CALL_CARD_UPDATE_IF_BACKUP)) {
        return true;
    }

    return false;
}


export function addSelfToCallCardMDT() {
    setCallCardRealtimeEnabled(false);
    const callCardId = App.currentCC.id;
    api.mdtAddSelfToCallCard(callCardId.toString(), result => {
        addMDTActiveCallCard(callCardId, false);

        window.OrnaReact.navigate.toCallCard(callCardId);
    }, errorResult => {
        setCallCardRealtimeEnabled(true);
    });
}


export function isCurrentUserPrimaryAndClearedOnMDTCallCard() {
    const userId = getCurrentUserId();
    const isPrimary = (App.currentCC.primaryOfficer === userId);
    const isCleared = (App.currentCC.primaryCleared !== null);
    return (isPrimary && isCleared);
}

export function SelfInitiate() {
    if (App.toolbarMDT) {
        App.toolbarMDT.enableItem("SelfInitiate", false);
        setTimeout(() => {
            if (App.toolbarMDT) {
                App.toolbarMDT.enableItem("SelfInitiate", true);
            }
        }, 2000);
    }

    const currentCardId = getMDTActiveCallCardId();
    if (hasNumericValue(currentCardId)) {
        window.OrnaReact.navigate.toCallCard(currentCardId, 'MDT');
        return;
    }

    // Show the self initiate modal
    showSelfInitiateModal();
}

let _statusUpdateModalContainer: HTMLElement | null = null;
let _statusUpdateModalOpen = false;
let _statusUpdateOfficers: { id: number; name: string }[] = [];

function _renderStatusUpdateModal() {
    if (!_statusUpdateModalContainer) {
        _statusUpdateModalContainer = document.createElement('div');
        document.body.appendChild(_statusUpdateModalContainer);
    }

    const canDispatchOfficerStatus =
        currentUserHasPermission(AccessAuthorities.ROLE_DISPATCH_OFFICER_STATUS) ||
        currentUserHasPermission(AccessAuthorities.ROLE_DISPATCH_ANY_STATUS);

    (window as any).OrnaReact.renderComponent(
        (window as any).StatusUpdateModalReact,
        {
            open: _statusUpdateModalOpen,
            officers: canDispatchOfficerStatus ? _statusUpdateOfficers : undefined,
            onUpdate: function (statusText: string) {
                _statusUpdateModalOpen = false;
                _renderStatusUpdateModal();
                updateStatusWithGeolocation(statusText);
            },
            onUpdateOfficer: canDispatchOfficerStatus ? function (statusText: string, targetPersonId: number) {
                _statusUpdateModalOpen = false;
                _renderStatusUpdateModal();
                api.statusUpdate({ statusComment: statusText, latitude: null, longitude: null, targetPersonId }, () => {
                    App.showPopupNotification('Officer status updated.', 'success');
                    window.dispatchEvent(new CustomEvent('orna:status-updated'));
                }, () => {
                    App.showPopupNotification('Unable to update officer status.', 'error');
                });
            } : undefined,
            onClear: function () {
                _statusUpdateModalOpen = false;
                _renderStatusUpdateModal();
                api.clearStatus(() => {
                    App.showPopupNotification('Status cleared.', 'success');
                    window.dispatchEvent(new CustomEvent('orna:status-updated'));
                }, () => {
                    App.showPopupNotification('Unable to clear status.', 'error');
                });
            },
            onClearOfficer: canDispatchOfficerStatus ? function (targetPersonId: number) {
                _statusUpdateModalOpen = false;
                _renderStatusUpdateModal();
                api.clearStatus(() => {
                    App.showPopupNotification('Officer status cleared.', 'success');
                    window.dispatchEvent(new CustomEvent('orna:status-updated'));
                }, () => {
                    App.showPopupNotification('Unable to clear officer status.', 'error');
                }, { targetPersonId });
            } : undefined,
            onCancel: function () {
                _statusUpdateModalOpen = false;
                _renderStatusUpdateModal();
            }
        },
        _statusUpdateModalContainer
    );
}

export function StatusUpdate() {
    _statusUpdateModalOpen = true;

    // If user has dispatch permission, fetch targetable users for the picker
    if (currentUserHasPermission(AccessAuthorities.ROLE_DISPATCH_OFFICER_STATUS) ||
        currentUserHasPermission(AccessAuthorities.ROLE_DISPATCH_ANY_STATUS)) {
        api.getStatusUpdateTargets((result: any) => {
            _statusUpdateOfficers = (result.targets || []).map((t: any) => ({
                id: t.id,
                name: t.name
            }));
            _renderStatusUpdateModal();
        }, () => {
            _statusUpdateOfficers = [];
            _renderStatusUpdateModal();
        });
    } else {
        _statusUpdateOfficers = [];
        _renderStatusUpdateModal();
    }
}

export enum MDTMenuCallButtonType {
    SELF_INITIATE,
    MY_CALL
}

export function updateMDTCallButton(callButtonType: MDTMenuCallButtonType) {
    const newText = callButtonType === MDTMenuCallButtonType.SELF_INITIATE ? "Self Initiate" : "My Call";

    // Update in toolbar component if it exists
    if (App.toolbarMDT && typeof App.toolbarMDT.updateItemText === 'function') {
        App.toolbarMDT.updateItemText("SelfInitiate", newText);
    }

}

export function setMDTActiveCallCard(activeCallCardId: number | undefined) {
    if (hasNumericValue(activeCallCardId)) {
        localStorage.setItem("CurrentCallCard", activeCallCardId.toString());
        localStorage.setItem(MDT_ACTIVE_CALL_TIMESTAMP_KEY, Date.now().toString());
        updateMDTCallButton(MDTMenuCallButtonType.MY_CALL);
    } else {
        localStorage.removeItem("CurrentCallCard");
        localStorage.removeItem(MDT_ACTIVE_CALL_TIMESTAMP_KEY);
        updateMDTCallButton(MDTMenuCallButtonType.SELF_INITIATE);
    }
}
export function addMDTActiveCallCard(activeCallCardId: number, overwriteExistingActiveCallCard: boolean = true) {
    // On initial module load, always accept the server data (even if null)
    if (isInitialMDTModuleLoad) {
        isInitialMDTModuleLoad = false;

        if (!hasValue(activeCallCardId)) {
            // Server says no active call - clear any stale data
            setMDTActiveCallCard(undefined);
            return;
        }

        const newCallCardId = parseInt(activeCallCardId.toString());
        if (isNaN(newCallCardId)) {
            setMDTActiveCallCard(undefined);
            return;
        }

        setMDTActiveCallCard(newCallCardId);
        return;
    }

    // After initial load, validate the input
    if (!hasValue(activeCallCardId)) {
        return;
    }

    const newCallCardId = parseInt(activeCallCardId.toString());
    if (isNaN(newCallCardId)) {
        return;
    }

    // If not overwriting and we have an existing call, don't update
    if (!overwriteExistingActiveCallCard) {
        const currentActiveCallCardId = getMDTActiveCallCardId();
        if (hasNumericValue(currentActiveCallCardId)) {
            return;
        }
    }

    setMDTActiveCallCard(newCallCardId);
}
export function addMDTActiveCallCardFromPubNub(activeCallCardId: number, updateToolbar: boolean = false) {
    if (!hasValue(activeCallCardId)) {
        return;
    }

    const newCallCardId = parseInt(activeCallCardId.toString());
    if (isNaN(newCallCardId)) {
        return;
    }

    // PubNub updates always win - they're real-time and authoritative
    setMDTActiveCallCard(newCallCardId);
}

export function removeMDTActiveCallCard(callCardIdToRemove: string | number = null) {
    let callCardIdToRemoveNum = null;
    if (hasValue(callCardIdToRemove)) {
        callCardIdToRemoveNum = isString(callCardIdToRemove) ? parseInt(<string>callCardIdToRemove) : callCardIdToRemove;
        const currentActiveCallCardId = getMDTActiveCallCardId();
        //Only remove the active call card if it's the one we want to remove.
        if (currentActiveCallCardId === callCardIdToRemoveNum) {
            setMDTActiveCallCard(undefined);
        }
    } else {
        //If not parameter passed on the call card ID to remove, force removal.
        setMDTActiveCallCard(undefined);
    }
}

export function getMDTActiveCallCardId(): number {
    return parseInt(localStorage.getItem("CurrentCallCard"));
}

export function loadMDTActiveCallCard() {
    const activeCallCardId = getMDTActiveCallCardId();
    
    if (hasNumericValue(activeCallCardId)) {
        // Check if the stored call is stale (older than 24 hours)
        const timestampStr = localStorage.getItem(MDT_ACTIVE_CALL_TIMESTAMP_KEY);
        if (timestampStr) {
            const timestamp = parseInt(timestampStr);
            if (!isNaN(timestamp)) {
                const age = Date.now() - timestamp;
                const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
                
                if (age > MAX_AGE) {
                    // Stale data - clear it
                    console.info('Clearing stale call assignment');
                    removeMDTActiveCallCard();
                    updateMDTCallButton(MDTMenuCallButtonType.SELF_INITIATE);
                    isInitialMDTModuleLoad = true; // Reset for next module selection
                    return;
                }
            }
        }
        
        updateMDTCallButton(MDTMenuCallButtonType.MY_CALL);
    } else {
        updateMDTCallButton(MDTMenuCallButtonType.SELF_INITIATE);
    }
    
    // Reset the flag - next time user selects MDT module, treat as initial load
    isInitialMDTModuleLoad = true;
}

// Window registrations
(window as any).MDT_Home = MDT_Home;
(window as any).MDT_Selected = MDT_Selected;
(window as any).MDT_OfficerStatus = MDT_OfficerStatus;
(window as any).MDT_ShowCallCard = MDT_ShowCallCard;
(window as any).SelfInitiate = SelfInitiate;
(window as any).StatusUpdate = StatusUpdate;
(window as any).addSelfToCallCardMDT = addSelfToCallCardMDT;
(window as any).goToSelfInitiatedCall = goToSelfInitiatedCall;
(window as any).addMDTActiveCallCard = addMDTActiveCallCard;
(window as any).addMDTActiveCallCardFromPubNub = addMDTActiveCallCardFromPubNub;
(window as any).removeMDTActiveCallCard = removeMDTActiveCallCard;
(window as any).getMDTActiveCallCardId = getMDTActiveCallCardId;
(window as any).loadMDTActiveCallCard = loadMDTActiveCallCard;
(window as any).updateMDTEditModeAllowed = updateMDTEditModeAllowed;
(window as any).MDTMenuCallButtonType = MDTMenuCallButtonType;
(window as any).updateMDTCallButton = updateMDTCallButton;
(window as any).setMDTActiveCallCard = typeof setMDTActiveCallCard !== 'undefined' ? setMDTActiveCallCard : undefined;
(window as any).mdtHasPermissionsToEditCallCard = typeof mdtHasPermissionsToEditCallCard !== 'undefined' ? mdtHasPermissionsToEditCallCard : undefined;
(window as any).showSelfInitiateModal = typeof showSelfInitiateModal !== 'undefined' ? showSelfInitiateModal : undefined;
